/* Kestrel Knives custom behaviour (Kestrel-written, no third-party code).
   One deferred file for all kestrel-* sections: countdowns, sign-up form trigger,
   Notify Me provider switch, sticky product sub-nav, free-shipping bar refresh. */
(() => {
  if (window.KestrelLoaded) return;
  window.KestrelLoaded = true;
  const pad = (n) => String(n).padStart(2, '0');

  /* Countdown: <kestrel-countdown data-target="ISO"> with [data-unit] spans */
  class KestrelCountdown extends HTMLElement {
    connectedCallback() {
      this.target = Date.parse(this.dataset.target || '');
      if (Number.isNaN(this.target)) { this.hidden = true; return; }
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const ms = this.target - Date.now();
      if (ms <= 0) {
        clearInterval(this.timer);
        this.setAttribute('expired', '');
        this.closest('[data-kestrel-drop]')?.setAttribute('data-expired', '');
        return;
      }
      const s = Math.floor(ms / 1000);
      const v = { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
      this.querySelectorAll('[data-unit]').forEach((el) => {
        const u = el.dataset.unit;
        el.textContent = u === 'd' ? v.d : pad(v[u]);
      });
    }
  }
  customElements.get('kestrel-countdown') || customElements.define('kestrel-countdown', KestrelCountdown);

  /* Open a Klaviyo sign-up form by ID; falls back to the element's href */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-kestrel-open-form]');
    if (!t) return;
    const id = t.dataset.kestrelOpenForm;
    if (id && (window._klOnsite || window.klaviyo)) {
      e.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', id]);
    }
  });

  /* Notify Me: re-sync with the server-rendered block after every variant change */
  class KestrelNotifyMe extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.onSelect = (event) => {
        if (!event.promise || event.target?.closest?.('product-card')) return;
        event.promise.then(({ detail }) => {
          const fresh = detail?.html?.querySelector(`kestrel-notify-me[data-block="${this.dataset.block}"]`);
          if (!fresh) return;
          this.dataset.available = fresh.dataset.available;
          this.dataset.variant = fresh.dataset.variant;
          this.innerHTML = fresh.innerHTML;
        }).catch(() => {});
      };
      this.section.addEventListener('shopify:product:select', this.onSelect);
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-kestrel-notify]');
        if (!b || this.dataset.provider !== 'event') return;
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('kestrel:notify-me', {
          bubbles: true,
          detail: { productId: this.dataset.product, variantId: this.dataset.variant, handle: this.dataset.handle }
        }));
      });
    }
    disconnectedCallback() { this.section?.removeEventListener('shopify:product:select', this.onSelect); }
  }
  customElements.get('kestrel-notify-me') || customElements.define('kestrel-notify-me', KestrelNotifyMe);

  /* Sticky product sub-nav: appears after the buy box scrolls away, highlights the active anchor */
  class KestrelSubnav extends HTMLElement {
    connectedCallback() {
      const trigger = document.querySelector(this.dataset.trigger || '.product-information') ;
      if (trigger && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([en]) => this.toggleAttribute('stuck', !en.isIntersecting && en.boundingClientRect.top < 0));
        this.io.observe(trigger);
      } else { this.setAttribute('stuck', ''); }
      const links = [...this.querySelectorAll('a[href^="#"]')];
      const targets = links.map((a) => document.getElementById(a.hash.slice(1))).filter(Boolean);
      links.forEach((a) => { if (!document.getElementById(a.hash.slice(1))) a.closest('li')?.remove(); });
      if (targets.length && 'IntersectionObserver' in window) {
        this.spy = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            links.forEach((a) => a.toggleAttribute('aria-current', a.hash === '#' + en.target.id));
          });
        }, { rootMargin: '-45% 0px -50% 0px' });
        targets.forEach((t) => this.spy.observe(t));
      }
    }
    disconnectedCallback() { this.io?.disconnect(); this.spy?.disconnect(); }
  }
  customElements.get('kestrel-subnav') || customElements.define('kestrel-subnav', KestrelSubnav);

  /* Free-shipping bar: refresh from /cart.js when the cart changes */
  class KestrelShippingBar extends HTMLElement {
    connectedCallback() {
      this.update = () => fetch((window.Shopify?.routes?.root || '/') + 'cart.js')
        .then((r) => r.json()).then((c) => this.render(c.total_price)).catch(() => {});
      document.addEventListener('cart:update', this.update);
      document.addEventListener('shopify:cart:lines-update', this.update);
    }
    disconnectedCallback() {
      document.removeEventListener('cart:update', this.update);
      document.removeEventListener('shopify:cart:lines-update', this.update);
    }
    render(total) {
      const goal = Number(this.dataset.goal);
      if (!goal) return;
      const left = Math.max(goal - total, 0);
      this.style.setProperty('--kestrel-progress', Math.min(total / goal, 1));
      this.toggleAttribute('done', left === 0);
      const amt = this.querySelector('[data-left]');
      if (amt) amt.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(left / 100);
    }
  }
  customElements.get('kestrel-shipping-bar') || customElements.define('kestrel-shipping-bar', KestrelShippingBar);

  /* Notify Me: show Klaviyo's back-in-stock trigger only when the Klaviyo embed is actually loaded;
     otherwise the theme-only email form stays visible. */
  const markKlaviyo = () => {
    if (!window.klaviyo) return false;
    document.querySelectorAll('kestrel-notify-me[data-provider="klaviyo"]').forEach((el) => el.classList.add('has-klaviyo'));
    return true;
  };
  window.addEventListener('load', () => { if (!markKlaviyo()) setTimeout(markKlaviyo, 2500); });
  /* 2026-11-01 Notify me scroll fix (V2 only). A plain href="#notify" jump puts the box at the very top of the screen,
     under the fixed header (and the product sub-nav once it is showing), so the heading and the email field were hidden.
     Every same-page #notify link (green Coming soon bar, sub-nav, sticky bar fallback, product-information link) and the
     #notify hash on load now scroll so the whole box sits just below whatever is stuck at the top, with 20px to spare.
     The offset is measured from the real header / sub-nav, so it follows mobile vs desktop and the announcement bar.
     Rollback: restore the 3-line block below that this replaces (see ROLLBACK.md). */
  const kScroller = (el) => {
    for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
      const oy = getComputedStyle(p).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1) return p;
    }
    return null;
  };
  const kCover = () => {
    /* stacked fixed/sticky bars at the top edge: header first, then the sub-nav that sits right under it when it is showing */
    const rects = [];
    document.querySelectorAll('header, #header-group, header-component, .k-subnav, [class*="announcement-bar"]').forEach((el) => {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        const pos = getComputedStyle(n).position;
        if (pos !== 'fixed' && pos !== 'sticky') continue;
        const r = n.getBoundingClientRect();
        if (r.height > 0 && r.bottom > 0) rects.push(r);
        break;
      }
    });
    rects.sort((x, y) => x.top - y.top);
    let bottom = 0;
    rects.forEach((r) => { if (r.top <= bottom + 2 && r.bottom > bottom) bottom = r.bottom; });
    return bottom;
  };
  const kNotifyScroll = (focusInput) => {
    const host = document.getElementById('notify');
    if (!host) return false;
    const box = host.querySelector('.k-notify') || host;
    const gap = 20;
    const sc = kScroller(host);
    const delta = () => box.getBoundingClientRect().top - (sc ? sc.getBoundingClientRect().top : 0) - kCover() - gap;
    const jump = (smooth) => {
      const d = delta();
      if (Math.abs(d) < 1) return;
      (sc || window).scrollBy({ top: d, left: 0, behavior: smooth ? 'smooth' : 'instant' });
    };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    jump(!reduce);
    /* wait for the smooth scroll to settle, then correct once for anything that changed meanwhile (the sub-nav sliding in) */
    const pos = () => (sc ? sc.scrollTop : window.scrollY);
    let last = pos(), still = 0, frames = 0;
    const settle = () => {
      frames += 1;
      const cur = pos();
      still = cur === last ? still + 1 : 0;
      last = cur;
      if (still < 8 && frames < 120) { requestAnimationFrame(settle); return; }
      jump(false);
      requestAnimationFrame(() => {
        jump(false);
        if (!focusInput) return;
        const input = host.querySelector('input[type="email"]');
        if (input && input.offsetParent !== null) input.focus({ preventScroll: true });
      });
    };
    requestAnimationFrame(settle);
    return true;
  };
  window.kestrelNotifyScroll = kNotifyScroll;
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target.closest && e.target.closest('a[href*="#notify"]');
    if (!a || a.target === '_blank') return;
    let u;
    try { u = new URL(a.getAttribute('href'), location.href); } catch (err) { return; }
    if (u.hash !== '#notify' || u.origin !== location.origin || u.pathname !== location.pathname) return;
    if (kNotifyScroll(true)) e.preventDefault();
  });
  if (location.search.includes('customer_posted=true') || location.hash === '#notify') {
    if (document.readyState === 'complete') kNotifyScroll(false); else window.addEventListener('load', () => kNotifyScroll(false));
  }

  /* Theme-only mega menu: open on hover (desktop pointer), click/tap and keyboard; one open at a time */
  const setNavTop = () => {
    const h = document.querySelector('.header__row--top') || document.querySelector('header');
    if (h) document.documentElement.style.setProperty('--k-nav-top', Math.max(0, h.getBoundingClientRect().bottom) + 'px');
    const nav = document.querySelector('[data-k-nav]');
    if (nav) nav.style.setProperty('--k-mega-left', (-nav.getBoundingClientRect().left) + 'px');
  };
  document.addEventListener('DOMContentLoaded', setNavTop);
  const closeAll = (except) => document.querySelectorAll('[data-k-mega].is-open').forEach((li) => {
    if (li === except) return;
    li.classList.remove('is-open');
    li.querySelector(':scope > a')?.setAttribute('aria-expanded', 'false');
  });
  document.addEventListener('mouseover', (e) => {
    const li = e.target.closest?.('[data-k-mega]');
    if (li) { setNavTop(); }
  }, { passive: true });
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-k-mega] > a');
    if (link && !matchMedia('(hover: hover)').matches) {
      const li = link.parentElement;
      if (!li.classList.contains('is-open')) {
        e.preventDefault(); setNavTop(); closeAll(li);
        li.classList.add('is-open'); link.setAttribute('aria-expanded', 'true');
        return;
      }
    }
    if (!e.target.closest('[data-k-mega]')) closeAll();
  });
  document.addEventListener('focusin', (e) => {
    const li = e.target.closest?.('[data-k-mega]');
    if (li) { setNavTop(); closeAll(li); li.classList.add('is-open'); li.querySelector(':scope > a')?.setAttribute('aria-expanded', 'true'); }
    else closeAll();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('[data-k-mega].is-open');
    if (open) { closeAll(); open.querySelector(':scope > a')?.focus(); }
  });
  window.addEventListener('scroll', () => { if (document.querySelector('[data-k-mega].is-open, [data-k-mega]:hover')) setNavTop(); }, { passive: true });
  window.addEventListener('resize', setNavTop, { passive: true });

  /* Theme-only mobile drawer. The panel is moved to <body> so the sticky header's containing block
     can't clip it; the summary button toggles html.k-drawer-open. */
  const drawerInit = () => {
    document.querySelectorAll('[data-k-drawer]').forEach((d) => {
      const panel = d.querySelector('.k-drawer__panel');
      if (!panel || panel.dataset.portaled) return;
      panel.dataset.portaled = '1';
      panel.id = panel.id || 'k-drawer-panel';
      document.body.appendChild(panel);
      const sum = d.querySelector('summary');
      sum.setAttribute('aria-controls', panel.id);
      sum.setAttribute('aria-expanded', 'false');
      const setOpen = (open) => {
        document.documentElement.classList.toggle('k-drawer-open', open);
        document.documentElement.style.overflow = open ? 'hidden' : '';
        sum.setAttribute('aria-expanded', String(open));
        if (open) panel.querySelector('button, a, summary')?.focus(); else sum.focus();
      };
      sum.addEventListener('click', (e) => { e.preventDefault(); setOpen(!document.documentElement.classList.contains('k-drawer-open')); });
      panel.addEventListener('click', (e) => { if (e.target.closest('[data-k-drawer-close]') || e.target === panel) setOpen(false); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && document.documentElement.classList.contains('k-drawer-open')) setOpen(false); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', drawerInit); else drawerInit();

  /* Slideshow: dots, autoplay (paused on hover/focus/hidden tab, off for reduced motion) */
  class KestrelSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.dots = [...this.querySelectorAll('[data-dot]')];
      if (!this.track || this.dots.length < 2) return;
      this.dots.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.dot))));
      this.track.addEventListener('scroll', () => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      }, { passive: true });
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.dataset.autoplay === 'true' && !reduce) {
        const ms = (Number(this.dataset.speed) || 7) * 1000;
        this.timer = setInterval(() => {
          if (this.matches(':hover, :focus-within') || document.hidden) return;
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.go((i + 1) % this.dots.length);
        }, ms);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    go(i) { this.track.scrollTo({ left: i * this.track.clientWidth, behavior: 'smooth' }); }
  }
  customElements.get('kestrel-slideshow') || customElements.define('kestrel-slideshow', KestrelSlideshow);
  /* Product row carousel: prev/next buttons scroll by one card; disabled at the ends.
     Markup: <kestrel-carousel><button data-k-prev> <ul data-k-track> <button data-k-next></kestrel-carousel> */
  class KestrelCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-k-track]');
      this.prev = this.querySelector('[data-k-prev]');
      this.next = this.querySelector('[data-k-next]');
      if (!this.track || !this.prev || !this.next) return;
      this.ac?.abort();
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };
      this.prev.addEventListener('click', () => this.step(-1), opts);
      this.next.addEventListener('click', () => this.step(1), opts);
      this.track.addEventListener('scroll', () => this.sync(), { ...opts, passive: true });
      window.addEventListener('resize', () => this.sync(), opts);
      this.sync();
      requestAnimationFrame(() => this.sync());
    }
    disconnectedCallback() { this.ac?.abort(); }
    stepSize() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }
    step(dir) {
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: dir * rtl * this.stepSize(), behavior: reduce ? 'auto' : 'smooth' });
    }
    sync() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const x = Math.abs(t.scrollLeft);
      const atStart = x <= 2;
      const atEnd = x >= max - 2;
      this.prev.disabled = atStart;
      this.next.disabled = atEnd;
      this.toggleAttribute('data-scrollable', max > 2);
      this.toggleAttribute('data-at-end', atEnd);
    }
  }
  customElements.get('kestrel-carousel') || customElements.define('kestrel-carousel', KestrelCarousel);

  /* FAQ: live search across questions + answers, hides empty categories, opens deep-linked question */
  const faqInit = () => {
    document.querySelectorAll('[data-k-faq]').forEach((root) => {
      const input = root.querySelector('[data-k-faq-search]');
      const items = [...root.querySelectorAll('[data-k-faq-item]')];
      const groups = [...root.querySelectorAll('[data-k-faq-group]')];
      const status = root.querySelector('[data-k-faq-status]');
      const empty = root.querySelector('[data-k-faq-empty]');
      const run = () => {
        const q = (input?.value || '').trim().toLowerCase();
        let shown = 0;
        items.forEach((it) => {
          const hit = !q || it.textContent.toLowerCase().includes(q);
          it.hidden = !hit;
          if (hit) shown++;
          if (q && hit) it.open = true;
        });
        groups.forEach((g) => { g.hidden = !g.querySelector('[data-k-faq-item]:not([hidden])'); });
        if (empty) empty.hidden = shown > 0;
        if (status) status.textContent = q ? `${shown} matching question${shown === 1 ? '' : 's'}` : '';
      };
      input?.addEventListener('input', run);
      const openHash = () => {
        const id = decodeURIComponent(location.hash.slice(1));
        const el = id && document.getElementById(id);
        if (el && el.matches('[data-k-faq-item]')) { el.open = true; el.scrollIntoView({ block: 'start' }); }
      };
      openHash();
      window.addEventListener('hashchange', openHash);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', faqInit); else faqInit();

  /* 2026-10-21 blank-page guard. From 990px up, base.css locks <html> (overflow:hidden, 100dvh) and .page-wrapper is
     the scroll container. The root can still be scrolled by code or by focus/scrollIntoView on an element laid out
     outside .page-wrapper (e.g. an absolutely positioned hidden input), and then the whole page slides out of view
     with no way for the shopper to scroll back. While the root is locked its scroll offset must always be 0. */
  const rootLockMq = window.matchMedia('(min-width: 990px)');
  const resetRootScroll = () => {
    const se = document.scrollingElement || document.documentElement;
    if (!rootLockMq.matches || (!se.scrollTop && !se.scrollLeft)) return;
    if (!document.querySelector('.page-wrapper')) return;
    if (getComputedStyle(document.documentElement).overflowY !== 'hidden') return;
    se.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };
  window.addEventListener('scroll', resetRootScroll, { passive: true });
  window.addEventListener('hashchange', resetRootScroll);
  window.addEventListener('pageshow', resetRootScroll);
  resetRootScroll();
})();
